import { Tooltip } from 'radix-ui';
import { BrowserRouter, Route, Routes } from 'react-router';
import { AuthGate } from './auth/AuthGate';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AuthProvider } from './auth/AuthProvider';
import { PartnerOnboardingGate } from './auth/PartnerOnboardingGate';
import { PageContent } from './PageContent';
import { UpdateBanner } from './components/UpdateBanner';
import { PageAuthAction } from './pages/Auth/PageAuthAction';
import { PageBookingOffer } from './pages/Booking/PageBookingOffer';
import { PageAcceptInvite } from './pages/Invite/PageAcceptInvite';
import { PagePrivacy } from './pages/Public/PagePrivacy';

// Auth: the app is gated behind sign-in (AuthGate), then behind partner
// registration (PartnerOnboardingGate). Admin routes additionally require
// super-admin status and partner routes an active partner account (see
// RequireAdmin / RequirePartner in PageContent).
//
// The onboarding gate sits outside the router on purpose. A partner manager who
// has not filled in their record and accepted the NDA sees nothing else —
// including the public catalogue, which is the very thing the NDA covers — so
// there is no route for it to be a guard on.
//
// Four routes sit outside both gates. Two are how somebody who cannot sign in
// gets to a state where they can: `/invite/:inviteId` creates an account, and
// `/auth/action` handles the links Firebase emails — password resets above all,
// which by definition reach someone locked out.
//
// `/booking/:requestId` is outside for a different reason. It is not a way in at
// all: a concierge answering a held date is authorised by the token in the link,
// and demanding a sign-in first would strand an offer that has a deadline on it
// behind whichever colleague happens to own the account.
//
// `/privacy` is outside because a privacy policy that only account holders can
// read is not published. It is also the one of the four that signed-in users
// reach by an ordinary link, from the footer — the route matches before the
// catch-all, so both audiences get the same page.
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Tooltip.Provider>
          {/* Above the boundary, not inside it: the tab most in need of a reload
              is the one whose bundle has just failed, and a banner that the
              crash takes down with it is no use on exactly that screen. */}
          <UpdateBanner />
          {/* Last resort. The public layout has its own boundary inside it, which
              catches a failing page while leaving the site around it usable; this
              one is for everything that has no layout to fall back into — the
              gates, the offer page, the admin CMS. */}
          <ErrorBoundary fullScreen>
            <Routes>
              <Route path='/invite/:inviteId' element={<PageAcceptInvite />} />
              <Route path='/booking/:requestId' element={<PageBookingOffer />} />
              <Route path='/auth/action' element={<PageAuthAction />} />
              <Route path='/privacy' element={<PagePrivacy />} />
              <Route
                path='*'
                element={
                  <AuthGate>
                    <PartnerOnboardingGate>
                      <PageContent />
                    </PartnerOnboardingGate>
                  </AuthGate>
                }
              />
            </Routes>
          </ErrorBoundary>
        </Tooltip.Provider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
